import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { FiArrowRight, FiArrowUp } from "react-icons/fi";
import { t as translate } from "../../i18n";
import { useSiteContent } from "../../content/SiteContentContext";

/**
 * Kelluva alareunan toimintapalkki
 * - CTA: "Pyydä tarjous" (scrollaa contactiin)
 * - "Ylös" (scrollaa sivun alkuun)
 * - piiloutuu scrollauksen aikana
 * - piiloutuu Contact-osiossa
 *
 * Props:
 * - lang   : "fi" | "en" | "es"
 * - prefix : "" | "/en" | "/es"
 */
const FloatingActionBar = ({ lang = "fi", prefix = "" }) => {
  const location = useLocation();
  const navigate = useNavigate();

  // Tilataan WordPressistä tulevan sisältödatan muutokset.
  // Labelit lasketaan jokaisella renderöinnillä, joten erillistä useMemoa ei tarvita.
  useSiteContent();

  const [isScrolledEnough, setIsScrolledEnough] = useState(false);
  const [isScrolling, setIsScrolling] = useState(false);
  const [isContactInView, setIsContactInView] = useState(false);
  const [isNearPageBottom, setIsNearPageBottom] = useState(false);
  const [isFooterInView, setIsFooterInView] = useState(false);

  const scrollStopTimerRef = useRef(null);
  const contactObserverRef = useRef(null);

  const fallback = {
    fi: {
      cta: "Pyydä tarjous",
      top: "Ylös",
      barAria: "Pikatoiminnot",
      ctaTitle: "Siirry yhteydenottolomakkeeseen",
      topTitle: "Takaisin sivun alkuun",
    },
    en: {
      cta: "Request a quote",
      top: "Top",
      barAria: "Quick actions",
      ctaTitle: "Go to contact form",
      topTitle: "Back to top",
    },
    es: {
      cta: "Solicitar presupuesto",
      top: "Arriba",
      barAria: "Acciones rápidas",
      ctaTitle: "Ir al formulario de contacto",
      topTitle: "Volver arriba",
    },
  };

  const base = fallback[lang] || fallback.fi;

  const pick = (key, fallbackText) => {
    const translationKey = `floatingActionBar.${key}`;
    const value = translate(translationKey, lang);
    return value === translationKey ? fallbackText : value;
  };

  const labels = {
    cta: pick("cta", base.cta),
    top: pick("top", base.top),
    barAria: pick("barAria", base.barAria),
    ctaTitle: pick("ctaTitle", base.ctaTitle),
    topTitle: pick("topTitle", base.topTitle),
  };

  // Etusivun polku nykyisellä kieliprefiksillä.
  const homePath = useMemo(() => {
    const cleanPrefix =
      typeof prefix === "string" ? prefix.replace(/\/+$/, "") : "";

    return cleanPrefix ? `${cleanPrefix}/` : "/";
  }, [prefix]);

  // Onko nykyinen route etusivu kieliprefiksistä riippumatta.
  const isOnHome = useMemo(() => {
    const pathname = location?.pathname || "/";
    const basePath = pathname.replace(/^\/(en|es)(?=\/|$)/, "") || "/";
    const normalized = basePath === "" ? "/" : basePath;

    return normalized === "/";
  }, [location?.pathname]);

  // Tarkista scroll-tila ja debounce "scroll loppui".
  useEffect(() => {
    const onScroll = () => {
      const y = window.scrollY || window.pageYOffset || 0;

      const documentHeight = Math.max(
        document.body.scrollHeight,
        document.documentElement.scrollHeight
      );
      const viewportHeight =
        window.innerHeight || document.documentElement.clientHeight;
      const bottomRemaining = documentHeight - (y + viewportHeight);

      // Piilota palkki aivan sivun lopussa, jotta footer ja copyright näkyvät.
      setIsNearPageBottom(bottomRemaining < 260);

      // Näytä palkki vasta kun käyttäjä on scrollannut vähän.
      setIsScrolledEnough(y > 420);

      // Piilota hetkeksi scrollauksen ajaksi.
      setIsScrolling(true);

      if (scrollStopTimerRef.current) {
        clearTimeout(scrollStopTimerRef.current);
      }

      scrollStopTimerRef.current = setTimeout(() => {
        setIsScrolling(false);
      }, 220);
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();

    return () => {
      window.removeEventListener("scroll", onScroll);

      if (scrollStopTimerRef.current) {
        clearTimeout(scrollStopTimerRef.current);
      }
    };
  }, []);

  // Havaitse näkyykö #contact viewportissa -> piilota CTA-palkki silloin.
  useEffect(() => {
    if (contactObserverRef.current) {
      contactObserverRef.current.disconnect();
      contactObserverRef.current = null;
    }

    const contactEl = document.getElementById("contact");

    if (!contactEl) {
      setIsContactInView(false);
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        setIsContactInView(Boolean(entry?.isIntersecting));
      },
      {
        // Kun noin 20 % Contact-sektiosta näkyy, pidetään sitä näkyvissä.
        threshold: 0.2,
      }
    );

    observer.observe(contactEl);
    contactObserverRef.current = observer;

    return () => observer.disconnect();
  }, [location.pathname]);

  // Havaitse näkyykö footer viewportissa -> piilota CTA-palkki silloin.
  useEffect(() => {
    const handleFooterVisibility = () => {
      const footer = document.querySelector("footer");

      if (!footer) {
        setIsFooterInView(false);
        return;
      }

      const footerTop = footer.getBoundingClientRect().top;
      const viewportHeight =
        window.innerHeight || document.documentElement.clientHeight;

      // Kun footer alkaa tulla näkyviin, alapalkki piilotetaan.
      setIsFooterInView(footerTop < viewportHeight - 40);
    };

    handleFooterVisibility();

    window.addEventListener("scroll", handleFooterVisibility, {
      passive: true,
    });
    window.addEventListener("resize", handleFooterVisibility);

    return () => {
      window.removeEventListener("scroll", handleFooterVisibility);
      window.removeEventListener("resize", handleFooterVisibility);
    };
  }, [location.pathname]);

  // Smooth scroll helper offsetilla, jotta navbar ei peitä kohdetta.
  const scrollToSection = useCallback((sectionId) => {
    const el = document.getElementById(sectionId);

    if (!el) return;

    const navOffset = 96;
    const top = el.getBoundingClientRect().top + window.pageYOffset - navOffset;

    window.scrollTo({
      top,
      behavior: "smooth",
    });
  }, []);

  const handleQuoteClick = useCallback(() => {
    if (isOnHome) {
      scrollToSection("contact");
      return;
    }

    // Jos ei olla etusivulla, siirrytään ensin sinne.
    navigate(homePath, { replace: false });

    // Pieni viive route-vaihdolle ja renderöinnille.
    setTimeout(() => {
      scrollToSection("contact");
    }, 320);
  }, [isOnHome, navigate, homePath, scrollToSection]);

  const handleScrollTop = useCallback(() => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  }, []);

  // Piilotusehdot:
  // - ei tarpeeksi scrollia
  // - käyttäjä scrollaa juuri nyt
  // - Contact-osio näkyvissä
  // - sivun loppu tai footer näkyvissä
  const hidden =
    !isScrolledEnough ||
    isScrolling ||
    isContactInView ||
    isNearPageBottom ||
    isFooterInView;

  return (
    <div
      aria-hidden={hidden}
      className={
        "fixed bottom-4 left-1/2 z-40 -translate-x-1/2 " +
        "w-[calc(100%-1rem)] sm:w-auto " +
        "transition-all duration-300 ease-out " +
        (hidden
          ? "pointer-events-none translate-y-4 opacity-0"
          : "pointer-events-auto translate-y-0 opacity-100")
      }
    >
      <div
        role="group"
        aria-label={labels.barAria}
        className={
          "mx-auto flex items-center gap-2 sm:gap-3 " +
          "rounded-2xl px-2 py-2 " +
          "border border-white/15 dark:border-white/10 " +
          "bg-white/80 dark:bg-gray-950/75 " +
          "backdrop-blur-xl " +
          "shadow-[0_12px_35px_rgba(0,0,0,0.18)] dark:shadow-[0_14px_40px_rgba(0,0,0,0.35)]"
        }
      >
        {/* CTA */}
        <button
          type="button"
          onClick={handleQuoteClick}
          title={labels.ctaTitle}
          aria-label={labels.ctaTitle}
          className={
            "inline-flex h-11 flex-1 items-center justify-center gap-2 sm:flex-none " +
            "rounded-xl px-4 sm:px-5 " +
            "text-sm font-bold sm:text-[15px] " +
            "bg-lime-400 text-black hover:bg-lime-300 " +
            "shadow-[0_0_0_1px_rgba(163,230,53,0.35)_inset] " +
            "transition-all duration-200 " +
            "focus:outline-none focus-visible:ring-2 focus-visible:ring-lime-300/70"
          }
        >
          <FiArrowRight />
          <span className="truncate">{labels.cta}</span>
        </button>

        {/* Ylös */}
        <button
          type="button"
          onClick={handleScrollTop}
          title={labels.topTitle}
          aria-label={labels.topTitle}
          className={
            "inline-flex h-11 items-center justify-center gap-2 " +
            "rounded-xl px-4 " +
            "text-sm font-semibold sm:text-[15px] " +
            "text-gray-900 dark:text-white " +
            "border border-gray-200/80 dark:border-white/10 " +
            "bg-white/70 hover:bg-white dark:bg-white/5 dark:hover:bg-white/10 " +
            "transition-all duration-200 " +
            "focus:outline-none focus-visible:ring-2 focus-visible:ring-lime-300/60"
          }
        >
          <FiArrowUp />
          <span>{labels.top}</span>
        </button>
      </div>
    </div>
  );
};

export default FloatingActionBar;